import {
  Button,
  Col,
  Divider,
  Form,
  Image,
  Input,
  message,
  Modal, Popconfirm,
  Row,
  Select,
  Switch, Tooltip, Upload,
} from 'antd';
import Card from 'antd/es/card/Card';
import {ReactNode, useEffect, useState} from 'react';
import {
  ArrowLeftOutlined,
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  PlusCircleOutlined,
  SaveOutlined,
  UploadOutlined
} from '@ant-design/icons';
import {useNavigate, useParams} from 'react-router-dom';
import HeaderBreadcrumb from '../../../../components/Breadcrumb';
import Loader from '../../../../components/Loader';
import createPageTitle from '../../../../utils/createPageTitle';
import {Helmet} from 'react-helmet';
import {BlogDetailRes} from '../../../../core/dtos/blogDetailRes';
import {createBlog, getBlog, updateBlog} from '../../../../services/blogService';
import {BlogCategory} from '../../../../core/enums/BlogCategory';
import TextArea from 'antd/es/input/TextArea';
import {generateImageUploadLink, uploadImage} from '../../../../services/uploadService';
import {GenerateImageUploadLinkRes} from '../../../../core/dtos/generateImageUploadLinkRes';
import getImageFromCdn from '../../../../utils/getImageFromCdn';
import ReactQuill from 'react-quill';
import textEditorOptions from '../../../../core/consts/textEditorOptions';

export default function Blog() {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const { id } = useParams();
  const [blog, setBlog] = useState<BlogDetailRes>({
    _id: '',
    createdBy: '',
    content: '',
    createdAt: '',
    title: '',
    images: [],
    comments: [],
    category: '',
    commentCount: 0,
    orderIndex: 0,
    permalink: '',
    isEnabled: false,
    excerpt: '',
    summary: ''
  });
  const isEdit = !!id;
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isEnabled, setIsEnabled] = useState<boolean>(true);
  const [blogPreviewContent, setBlogPreviewContent] = useState<ReactNode>();
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isImageLoading, setIsImageLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isEdit)
      getBlog(id)
        .then((body: BlogDetailRes) => {
          form.setFieldsValue({
            ...body,
          });
          setIsEnabled(body.isEnabled);
          setBlog(body);
          setUploadedImages(body.images);
        })
        .finally(() => {
          setIsLoading(false);
        });
    else {
      setIsLoading(false);
    }
  }, []);

  const handleSubmit = () => {
    if (isEdit) {
      updateBlog({
        ...form.getFieldsValue(),
        _id: blog._id,
        isEnabled,
        images: uploadedImages
      })
        .then(() => {
          navigate('/blogs');
          messageApi.open({
            type: 'success',
            content: 'Blog successfully updated',
          });
        }
        ).catch((err) => {
          messageApi.open({
            type: 'error',
            content: 'There is a problem',
          });
        });
    } else {
      createBlog({
        ...form.getFieldsValue(),
        isEnabled,
        images: uploadedImages
      })
        .then(() => {
          navigate('/blogs');
          messageApi.open({
            type: 'success',
            content: 'Blog successfully created',
          });
        }
        ).catch(() => {
          messageApi.open({
            type: 'error',
            content: 'There is a problem',
          });
        });
    }
  };

  const handleUploadImage = (file: any) => {
    if (file) {
      setIsImageLoading(true);

      generateImageUploadLink({fileNameWithExtension: file.name})
        .then((generatedBody: GenerateImageUploadLinkRes) => {
          uploadImage(generatedBody.uploadLink, file)
            .then(() => {
              setUploadedImages([
                ...uploadedImages,
                generatedBody.fileName
              ]);
            })
            .catch((err) => {
              messageApi.open({
                type: 'error',
                content: err.response.data.message,
              });
            })
            .finally(() => {
              setIsImageLoading(false);
            });
        })
        .catch((err) => {
          setIsImageLoading(false);
          messageApi.open({
            type: 'error',
            content: err.response.data.message,
          });
        });
    }
  };

  const handleRemoveImage = (key: number) => {
    const uploadedImagesClone = [...uploadedImages];
    uploadedImagesClone.splice(key, 1);

    setUploadedImages(uploadedImagesClone);
  };

  if (isLoading) {
    return (
      <Loader />
    );
  }
  return (
    <>
      <Helmet>
        <title>{createPageTitle(blog?.title || 'Create Blog')}</title>
      </Helmet>
      {contextHolder}
      <HeaderBreadcrumb items={['Blogs', 'Create/Edit Blog']} />
      <Card title={isEdit ? 'Edit Blog' : 'Create Blog'}>
        <Form name='Blog' form={form} onFinish={handleSubmit} layout='vertical'>
          <Row gutter={[24, 0]}>
            <Col xs={24}>
              <Form.Item
                name='title'
                rules={[{ required: true, message: 'Please enter a title' }, { min: 3, message: 'Title must be longer than 2 characters' }]}
                label='Title'
              >
                <Input placeholder="Title" size='large' />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name='summary'
                rules={[{ required: true, message: 'Please enter a summary' }, { min: 3, message: 'Summary must be longer than 2 characters' }]}
                label='Summary'
              >
                <TextArea placeholder="Summary (short description)" size='large' />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name='content'
                rules={[{ required: true, message: 'Please enter a content' }]}
                label='Content'
              >
                <ReactQuill
                  theme="snow"
                  formats={textEditorOptions.formats}
                  modules={textEditorOptions.modules} />
              </Form.Item>
              <Button type="primary" size="small" style={{marginLeft: 'auto', display: 'flex', marginTop: '-10px'}} onClick={() => setBlogPreviewContent(form.getFieldValue('content'))}>
                  Preview
              </Button>
            </Col>
            <Col xs={12}>
              <Form.Item
                name='category'
                rules={[{ required: true, message: 'Please select a category' }]}
                label='Category'
              >
                <Select
                  size='large'
                  style={{ width: '100%' }}
                  placeholder="Select the associated category"
                  options={[
                    {
                      value: BlogCategory.NEWS_SUMMARY,
                      label: 'News Summary'
                    },
                    {
                      value: BlogCategory.UNCATEGORIZED,
                      label: 'Uncategorized'
                    }
                  ]}
                  filterOption={(input: string, option: any) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={12}>
              <Form.Item
                name='excerpt'
                label='Excerpt'
              >
                <Input placeholder="Excerpt" size='large' style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Row>
                <Col xs={24}>
                  <Form.Item
                    label='Thumbnail'
                  >
                    <Upload customRequest={(event) => handleUploadImage(event.file)}>
                      <Button icon={<UploadOutlined />}>Click to Upload</Button>
                    </Upload>
                  </Form.Item>
                </Col>
                {uploadedImages && (
                  <Col xs={24} style={{marginBottom: '24px'}}>
                    <div style={{display: 'flex', gap: '16px', flexWrap: 'wrap'}}>
                      {uploadedImages?.map((image, key) => (
                        <Card
                          key={key}
                          style={{ padding: '0' }}
                          actions={[
                          // eslint-disable-next-line react/jsx-key
                            <Popconfirm
                              placement="bottomRight"
                              title='Delete the Image'
                              description='Are you sure to delete this image?'
                              onConfirm={() => {
                                handleRemoveImage(key);
                              }}
                              okText="Delete"
                              cancelText="Cancel"
                            >
                              <Tooltip title="Delete Image">
                                <DeleteOutlined key="ellipsis" style={{ color: 'orangered', fontSize: '16px' }} />
                              </Tooltip>
                            </Popconfirm>,
                          ]}
                        >
                          <Image
                            width={140}
                            height={140}
                            src={getImageFromCdn(image)}
                            // fallback="data:image/png;base64,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"
                            style={{objectFit: 'cover'}}
                          />
                        </Card>
                      ))}
                      {isImageLoading && (
                        <Card
                          style={{ padding: '0' }}
                          actions={[
                            <DeleteOutlined key="delete" />,
                          ]}
                        >
                          <Image
                            width={140}
                            height={140}
                            src="data:image/png;base64,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"
                            style={{objectFit: 'cover'}}
                          />
                        </Card>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
            <Col xs={24} style={{display: 'flex', alignItems:'center', gap:'8px'}}>
              <Form.Item
                name='isEnabled'
                label='Is Enable'
              >
                <Switch
                  checkedChildren={<CheckOutlined />}
                  unCheckedChildren={<CloseOutlined />}
                  id="is-enabled"
                  onChange={(e) => setIsEnabled(e)}
                  defaultChecked={isEnabled}
                />
                <label htmlFor='is-enabled' style={{marginLeft: '8px'}}>
                  <b>{isEnabled ? 'Enabled' : 'Disabled'}</b>
                </label>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Divider />
            </Col>
            <Col xs={24} style={{gap: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '24px'}}>
              <Button type="primary" shape="default" icon={<ArrowLeftOutlined />} size="large" danger style={{width: '200px'}} onClick={() => navigate(-1)}>
            Back
              </Button>
              <Button type="primary" htmlType='submit' shape="default" icon={isEdit ? <SaveOutlined /> : <PlusCircleOutlined />} size="large" style={{width: '200px'}}>
                {isEdit ? 'Save' : 'Create'}
              </Button>
            </Col>
          </Row>
        </Form>
      </Card>

      <Modal
        centered
        title="Blog Content Preview"
        open={!!blogPreviewContent}
        onOk={() => setBlogPreviewContent('')}
        onCancel={() => setBlogPreviewContent('')}
        width={800}
        style={{marginTop: '28px', marginBottom: '28px'}}
      >
        <div className="et_pb_post_content_0_tb_body" dangerouslySetInnerHTML={{ __html: form.getFieldValue('content') }} />
      </Modal>
    </>
  );
}
